import { Box, Grid, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, ListSubheader, Switch, TextField, Typography } from "@material-ui/core";
import { AttachMoney, Check, Delete, MoneyOff, Save } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import React, { useRef, useState } from "react";
import { Route, Switch as RouterSwitch, useHistory } from "react-router-dom";

import { Collections } from "Collections";
import { useAppUser } from "components/AppUserProvider";
import { NoSubscriptionAlert } from "components/NoSubscriptionAlert";
import { useOrgUser } from "components/OrgUserProvider";
import { PageNavigation } from "components/PageNavigation";
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils";
import { useSubscriptionAccess } from "hooks/useSubscriptionStatus";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { DbDoc, WithRef } from "models/FbDoc";
import { Grant } from "models/Grant";
import { Nullable } from "models/Nullable";
import { Offer } from "models/Offer";
import { getOrgUserGrants } from "models/OrgUser";
import { Paths } from "Paths";
import { AddressInput } from "screens/MyOrgScreen";
import { calculateDiscountFromPercentage, calculateDiscountPercentage } from "Util/calculatePrices";
import { handleEnterKey } from "Util/handleEnterKey";
import { isEmpty } from "Util/isEmpty";
import { isNumber } from "Util/isNumber";
import { ReplaceType } from "Util/ReplaceType";
import { toIdMap } from "Util/toIdMap";

import { createdByItem, DeleteModal, OfferActions, sendOfferEmail } from "../OfferScreen";
import { getOfferTotalTime, getTotalTimeString } from "./InternalOfferAccounting";
import { EditableContactInfo } from "./OfferContactInfo";
import { offerNavigation } from "./OfferRoomCreationScreen";
import { OfferSummaryOverviewLinks, OfferSummarySites } from "./OfferSummarySites";
import { formatMoney } from "./SummaryTable";

type Props = {
    offer: Offer
    setOffer: (offer: EditabeOffer) => void
    isSaved: boolean
    path: string
    phone: string
    formIsValid: boolean
    manualPrice: string
    discount: string
    hasChanges: boolean
    offerDoc?: WithRef<DbDoc<Offer>>
}

export function stringIfEmpty(value?: Nullable<string>): string {
    return isEmpty(value) ? '' : value
}

export type DiscountsButtonProps = {
    subTotal: number
    manualPrice?: Nullable<number>
    discounts: number[]
    onClick: (discount: number) => void
}

export function DiscountButtons({ subTotal, manualPrice, discounts, onClick }: DiscountsButtonProps) {
    return (
        <Grid container direction="row" justify="space-evenly">
            {discounts.map(discount => {
                const discountValue = calculateDiscountFromPercentage({
                    subTotal,
                    manualPrice,
                    discountPercentage: discount,
                })
                return (
                    <Grid item key={String(discount)}>
                        <IconButton color="secondary" size="small" onClick={() => onClick(discountValue)}>-{(discount)}%</IconButton>
                    </Grid>
                )
            })}
        </Grid>
    )
}

type EditabeOffer = Partial<ReplaceType<ReplaceType<Offer, 'contactInfo', EditableContactInfo>, 'price', Partial<{ discount: string, manualPrice: string }>>>

export function OfferSummaryScreen({ offer, setOffer, isSaved, hasChanges, path, formIsValid, manualPrice, discount, phone, offerDoc }: Props) {
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar()
    const [appUserDoc] = useAppUser()
    const [orgUserDoc] = useOrgUser()
    const [subscriptionAccess] = useSubscriptionAccess()

    const appUser = appUserDoc?.data || null
    const orgUser = orgUserDoc?.data || null

    const history = useHistory();

    const validOffers = Object.values(offer.offerRooms).filter(offerRoom => Object.keys(offerRoom.priceFields).length > 0);


    const orgUserGrants = getOrgUserGrants(orgUser)
    const canCreateOffer = orgUserGrants[Grant.CREATE_OFFER] === true
    const canSendEmail = orgUserGrants[Grant.SEND_OFFER] === true

    const [sendEmail, setSendEmail] = useState(false)

    function saveAndExit(asDraft: boolean) {
        if (!appUser || !orgUser) {
            return
        }
        if (asDraft && isSaved) {
            FirebaseUtils.updateDocument<Offer>(
                true,
                Collections.OFFER_DRAFTS,
                offer.id,
                appUser,
                orgUser,
                AnalyticsEvent.UPDATED_OFFER_DRAFT,
                AnalyticsEvent.UPDATED_OFFER_DRAFT,
                AnalyticsEvent.UPDATE_OFFER_DRAFT_ERROR,
                enqueueSnackbar,
                'Lagret!',
                {
                    ...offer,
                    offerRooms: asDraft ? offer.offerRooms : toIdMap(validOffers),
                },
                (doc) => ({
                    offerDraftId: doc.id,
                }),
            )
            history.push(Paths.OFFER_DRAFTS + '/' + offer.id, { ignoreUnsavedChanges: true })
        } else if (asDraft || canCreateOffer) {
            const newId = FirebaseUtils.createDocument<Offer>(
                true,
                asDraft ? Collections.OFFER_DRAFTS : Collections.OFFERS,
                (!isSaved || asDraft) ? null : offer.id,
                appUser,
                orgUser,
                asDraft ? AnalyticsEvent.CREATED_OFFER_DRAFT : AnalyticsEvent.CREATED_OFFER,
                asDraft ? AnalyticsEvent.CREATED_OFFER_DRAFT : AnalyticsEvent.CREATED_OFFER,
                asDraft ? AnalyticsEvent.CREATE_OFFER_DRAFT_ERROR : AnalyticsEvent.CREATE_OFFER_ERROR,
                enqueueSnackbar,
                asDraft ? 'Lagret anbudet som utkast' : 'Fullførte anbudet' + (sendEmail ? ' og sendte epost til kunden' : ''),
                {
                    ...offer,
                    offerRooms: asDraft ? offer.offerRooms : toIdMap(validOffers),
                },
                (doc) => ({
                    offerDraftId: doc.id,
                }),
            )
            if (!asDraft && isSaved) {
                FirebaseUtils.deleteDocument(
                    true,
                    Collections.OFFER_DRAFTS,
                    offer.id,
                    appUser,
                    orgUser,
                    AnalyticsEvent.DELETED_OFFER_DRAFT,
                    AnalyticsEvent.CREATED_OFFER,
                    AnalyticsEvent.DELETE_OFFER_DRAFT_ERROR,
                    enqueueSnackbar,
                    'Utkast slettet',
                    (doc) => ({ offerDraftId: doc.id }),
                )
            }
            if (!asDraft && sendEmail) {
                sendOfferEmail(newId, offer.org.id, appUser, orgUser, enqueueSnackbar, null)
            }
            history.push((asDraft ? Paths.OFFER_DRAFTS : Paths.OFFER) + '/' + newId, { ignoreUnsavedChanges: true })
        }

    }

    const { contactInfo } = offer

    function setContactInfo(changes: Partial<EditableContactInfo>) {
        setOffer({
            contactInfo: changes,
        })
    }

    const commentRef = useRef<any>();
    const manualPriceRef = useRef<any>();
    const discountRef = useRef<any>();

    const parentPath = path.slice(0, path.lastIndexOf('/'))

    const totalTime = getOfferTotalTime(validOffers)

    return (
        <RouterSwitch>
            <Route exact path={path}>
                <PageNavigation
                    hasUnsavedChanges={hasChanges}
                    safeUnsavedNavigation={[parentPath, path + '/enkel-oppsummering', path + '/detaljert-oppsummering', path + '/internt-regnskap', path + '/kontaktinfo']}
                    title="Send Anbud"
                    rightAppBar={(
                        <IconButton color="inherit" aria-label="vis/gjem enhetspriser" onClick={() => setOffer({ hideUnitPrices: !offer.hideUnitPrices })}>
                            {offer.hideUnitPrices ? <MoneyOff /> : <AttachMoney />}
                        </IconButton>
                    )}

                    bottomNavigation={offerNavigation(parentPath)}
                    backHistoryButton>
                    {(deleteModalOpen && offer.id && isSaved) ? (
                        <DeleteModal
                            onClose={() => setDeleteModalOpen(false)}
                            onDelete={() => {
                                FirebaseUtils.deleteDocument(
                                    true,
                                    Collections.OFFER_DRAFTS,
                                    offer.id,
                                    appUser,
                                    orgUser,
                                    AnalyticsEvent.DELETED_OFFER_DRAFT,
                                    AnalyticsEvent.DELETED_OFFER_DRAFT,
                                    AnalyticsEvent.DELETE_OFFER_DRAFT_ERROR,
                                    enqueueSnackbar,
                                    'Slettet utkastet',
                                    () => ({ offerId: offer.id, }),
                                )
                                history.push(Paths.OFFER_DRAFTS)
                            }}
                        />
                    ) : null}
                    <List dense>
                        {createdByItem(offer.offerCreator.orgUser)}
                    </List>
                    <OfferSummaryOverviewLinks
                        path={path}
                        contactInfoBadge={[
                            !offer.contactInfo?.name,
                            !offer.contactInfo?.email || !offer.contactInfo?.phone,
                        ].reduce((acc, condition) => condition ? acc + 1 : acc, 0)}
                    />
                    <List subheader={<ListSubheader>Adresse</ListSubheader>}>
                        <ListItem>
                            <AddressInput
                                value={offer.address}
                                onChange={(newAddress) => {
                                    setOffer({ address: newAddress })
                                    commentRef.current?.focus()
                                }}
                            />
                        </ListItem>
                    </List>
                    <List subheader={<ListSubheader>Pris</ListSubheader>}>
                        <ListItem>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Manuell Pris"
                                type="number"
                                inputRef={manualPriceRef}
                                onWheel={event => event.currentTarget.blur()}
                                value={stringIfEmpty(manualPrice)}
                                onKeyDown={handleEnterKey((e) => (e.target as unknown as any).blur())}
                                onChange={e => {
                                    setOffer({
                                        price: {
                                            manualPrice: e.target.value,
                                        },
                                        hideUnitPrices: !!e.target.value,
                                    })
                                }}
                                InputProps={{
                                    endAdornment: isNumber(offer.price.manualPrice) ? `${Math.round(((offer.price.manualPrice || 0) * 100) / offer.price.subTotal)}%` : null,
                                }}
                            />
                        </ListItem>
                        <ListItem>
                            <DiscountButtons subTotal={offer.price.subTotal} manualPrice={offer.price.manualPrice} discounts={[5, 10, 15, 20, 25, 30]} onClick={(disc) => setOffer({ price: { discount: String(disc) } })} />
                        </ListItem>
                        <ListItem>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Rabatt"
                                type="number"
                                onWheel={event => event.currentTarget.blur()}
                                inputRef={discountRef}
                                value={stringIfEmpty(discount)}
                                onKeyDown={handleEnterKey((e) => (e.target as unknown as any).blur())}
                                onChange={e => {
                                    const val = e.target.value
                                    const numVal = Number(val)
                                    if (isNumber(numVal) && (numVal < 0)) {
                                        return
                                    } else {
                                        setOffer({
                                            price: {
                                                discount: e.target.value,
                                            }
                                        })
                                    }
                                }}
                                InputProps={{
                                    endAdornment: `${Math.round(calculateDiscountPercentage({ finalPrice: offer.price.finalPrice, manualPrice: offer.price.manualPrice, discount: offer.price.discount }))}%`,
                                }}
                            />
                        </ListItem>
                        <ListItem dense>
                            <ListItemText
                                primary="Totalt ekskl. mva."
                                secondary={formatMoney(offer.price.finalPrice)}
                            />
                        </ListItem>
                        {totalTime ? (
                            <ListItem dense>
                                <ListItemText
                                    primary="Estimert tid"
                                    secondary={getTotalTimeString(totalTime)}
                                />
                            </ListItem>
                        ) : null}
                    </List>
                    <List subheader={<ListSubheader>Fullfør</ListSubheader>}>
                        <ListItem>
                            <TextField
                                multiline
                                variant="outlined"
                                fullWidth label="Kommentar"
                                type="text"
                                inputRef={commentRef}
                                value={stringIfEmpty(offer.comment)}
                                onChange={e => setOffer({ comment: e.target.value })}
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemText is="switch-list-label-show-unit-prices" primary="Vis enhetspriser" />
                            <ListItemSecondaryAction>
                                <Switch color="secondary" onChange={e => setOffer({ hideUnitPrices: !e.target.checked })} checked={!offer.hideUnitPrices} />
                            </ListItemSecondaryAction>
                        </ListItem>
                        {canSendEmail ? (
                            <ListItem>
                                <ListItemText is="switch-list-label-email" primary="Send på e-post" />
                                <ListItemSecondaryAction>
                                    <Switch
                                        color="secondary"
                                        onChange={e => setSendEmail(e.target.checked)}
                                        checked={sendEmail}
                                    />
                                </ListItemSecondaryAction>
                            </ListItem>
                        ) : null}
                        <ListItem>
                            {formIsValid ? null : (
                                <Box mx="auto">
                                    <Typography variant="subtitle2" color="secondary">Du må fylle ut kontaktinformasjon for å fullføre anbudet</Typography>
                                </Box>
                            )}
                        </ListItem>
                        <NoSubscriptionAlert showPaymentManager />
                        <OfferActions
                            orgUserGrants={orgUserGrants}
                            actions={[
                                (appUser && orgUser) ? [[], 2, <Check />, canCreateOffer ? 'Fullfør og lås' : 'Kun Eiere kan fullføre', () => {
                                    saveAndExit(false)
                                }, { disabled: !formIsValid || !canCreateOffer || !subscriptionAccess, color: 'primary' }] : null,
                                (appUser && orgUser) ? [[], 1, <Save />, 'Lagre', () => {
                                    saveAndExit(true)
                                }, { disabled: !hasChanges || !subscriptionAccess, color: 'primary' }] : null,
                                isSaved ? [[], 2, <Delete />, 'Slett', () => setDeleteModalOpen(true)] : null,
                            ]}
                        />
                    </List>
                </PageNavigation>
            </Route>
            <OfferSummarySites
                offerId={offer.id}
                path={path}
                hasChanges={hasChanges}
                hideUnitPrices={offer.hideUnitPrices}
                setHideUnitPrices={hide => setOffer({ hideUnitPrices: hide })}
                offerRooms={offer.offerRooms}
                offerRoomComments={offer.offerRoomComments}
                setOfferRoomComments={newComments => {
                    setOffer({
                        offerRoomComments: newComments,
                    })
                }}
                offerRoomNames={offer.offerRoomNames}
                discount={offer.price.discount}
                manualPrice={offer.price.manualPrice}
                phone={phone}
                contactInfo={contactInfo}
                setContactInfo={setContactInfo}
            />
        </RouterSwitch>
    )
}
